import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useContext, useMemo } from 'react';
import { AlertContext } from '../contexts/alert-context';
import type { Vendor, VendorIntegration } from '../data/vendor';
import { vendorIntegrationsQueryOptions, vendorsQueryOptions } from '../query/query-options';
import { storeLayout } from '../router';
import { useStoreService } from './use-store-service';

export function useVendors() {
  const { addErrorAlert } = useContext(AlertContext);
  const queryClient = useQueryClient();
  const { store, storeService } = storeLayout.useRouteContext();

  const { data: vendors = [], isLoading } = useQuery({
    ...vendorsQueryOptions(store.id),
    queryFn: async () => {
      return storeService.getVendorsByStore(store.id).catch((error: Error) => {
        addErrorAlert(`Invalid vendors: ${error.message}`);

        throw error;
      });
    },
  });

  const updateVendor = useCallback(
    (vendor: Vendor) => {
      return storeService
        .updateVendor(vendor.store_id, vendor)
        .then((updatedVendor: Vendor) => {
          queryClient.setQueryData(vendorsQueryOptions(store.id).queryKey, (old: Vendor[] = []) =>
            old.map((v) => (v.vendor_id === updatedVendor.vendor_id ? updatedVendor : v))
          );

          return updatedVendor;
        })
        .catch((error: Error) => {
          addErrorAlert(error.message);

          throw error;
        });
    },
    [storeService, addErrorAlert, store, queryClient]
  );

  return {
    vendors,
    // Only show loading if we don't have data AND we're actually loading (not just fetching)
    loading: vendors.length === 0 && isLoading,
    updateVendor,
  };
}

export function useVendor(id: string | undefined) {
  const { vendors, loading } = useVendors();
  const vendor = useMemo(() => vendors.find((vendor) => vendor.vendor_id === id), [vendors, id]);

  return { loading, vendor };
}

// useStoreVendors fetches vendors for any store by id, without depending on
// storeLayout.useRouteContext(). Used by admin pages that aren't scoped to
// the signed-in user's store (e.g. the onboarding timeline). Shares cache with
// useVendors() for the same store via the shared queryKey.
export function useStoreVendors(storeId: string) {
  const storeService = useStoreService();

  const { data: vendors = [], isLoading } = useQuery({
    ...vendorsQueryOptions(storeId),
    queryFn: () => storeService.getVendorsByStore(storeId),
  });

  return {
    vendors,
    loading: vendors.length === 0 && isLoading,
  };
}

// useStoreVendorIntegrations fetches the integrations catalog for any store
// by id, without depending on storeLayout.useRouteContext(). Used by admin
// pages that aren't scoped to the signed-in user's store. Shares cache with
// useVendorIntegrations() for the same store via the shared queryKey.
export function useStoreVendorIntegrations(storeId: string) {
  const storeService = useStoreService();

  const { data: integrations = [] } = useQuery({
    ...vendorIntegrationsQueryOptions(storeId),
    queryFn: () => storeService.getVendorIntegrations(storeId),
  });

  return integrations;
}

export function useReenableRepresentative(storeId: string, vendorId: string) {
  const storeService = useStoreService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (email: string) => storeService.reEnableRepresentative(storeId, vendorId, email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vendorsQueryOptions(storeId).queryKey });
    },
  });
}

export function useVendorIntegrations(): VendorIntegration[] {
  const { store, storeService } = storeLayout.useRouteContext();

  const { data: integrations = [] } = useQuery({
    ...vendorIntegrationsQueryOptions(store.id),
    queryFn: () => storeService.getVendorIntegrations(store.id),
  });

  return integrations;
}
